import { get, writable } from "svelte/store";
import { RandomColor } from "../../../../lib/RandomColors";
import { Dictionary } from "../../../../shared/Dictionary";
import * as turf from "@turf/turf";
import { appManager } from "../../../../AppManager";
import { v4 as uuidv4 } from "uuid";
import { MatrixRowManager } from "./matrices/MatrixRowManager";
import { TripsScenarioMapManager } from "./TripsScenarioMapManager";
import { TripsScenarioSegmentWorker } from "./TripsScenarioSegmentWorker";
/**
 * Contains all trips for a trips layer.
 */
export class TripsScenarioManager {
    constructor(parent, data, options) {
        this.assignmentRows = writable([]);
        this.assignmentRowsPerMatrix = new Dictionary();
        this.expandedStore = writable(true);
        this.profilesLoaded = false;
        this.parent = parent;
        this.data = data;
        this.options = options;
        this.segmentWorker = new TripsScenarioSegmentWorker(this, this.getBranchCommitIdStore());
        this.segmentReferenceWorker = new TripsScenarioSegmentWorker(this, this.getReferenceBranchCommitId());
        this.mapLayerManager = new TripsScenarioMapManager(this.data.id, this, this.segmentReferenceWorker, this.segmentWorker, this.assignmentRows);
        this.parent.getBranchCommitIdStore().subscribe(_ => {
            this.refreshTrips();
        });
        this.parent.getReferenceBranchCommitIdStore().subscribe(_ => {
            this.refreshTrips();
        });
        this.name = writable(this.data.name);
        this.name.subscribe(v => {
            if (this.data.name == v)
                return;
            this.data.name = v;
            this.save().then();
        });
        this.profiles = writable([]);
        const matrices = [];
        this.data.matrices.forEach(x => {
            matrices.push(new MatrixRowManager(this, x));
        });
        this.matrices = writable(matrices);
        this.onMatricesChanged(matrices);
        this.refreshTrips();
    }
    refreshTrips() {
        this.segmentWorker.abort();
        this.segmentReferenceWorker.abort();
        this.mapLayerManager.clearFeatures();
        this.segmentWorker.refresh().then();
        this.segmentReferenceWorker.refresh().then();
    }
    getData() {
        return this.data;
    }
    addToMap(map) {
        if (typeof this.map !== "undefined")
            return;
        this.map = map;
        this.mapLayerManager.addToMap(map);
        get(this.matrices).forEach(x => x.addToMap(map));
        this.matrices.subscribe(ms => {
            this.onMatricesChanged(ms);
        });
        this.refreshTrips();
    }
    getIsLeftSideDriveStore() {
        return this.parent.getIsLeftSideDriveStore();
    }
    /**
     * Gets the parent.
     *
     * @returns The parent.
     */
    getParent() {
        return this.parent;
    }
    getExpandedStore() {
        return this.expandedStore;
    }
    /**
     * Gets the count for a given od-pair and a profile.
     *
     * @param vehicleType The routing profile.
     * @param movementKey The key (an od-pair).
     */
    getCount(profile, movementKey) {
        let count = 0;
        get(this.matrices)?.forEach(matrix => {
            count += matrix.getCount(profile, movementKey);
        });
        return count;
    }
    async onAddNewMatrixClick() {
        if (typeof this.map === "undefined")
            return;
        const view = this.map.getView();
        const result = await appManager.ModalHooks.NewOdModal.open(view.center);
        if (result === "none")
            return;
        if (result === "new") {
            const newMatrix = {
                id: `unsaved-${uuidv4()}`,
                name: "Custom Matrix",
                locations: [],
                elements: [],
                properties: {
                    "source": "manual"
                },
                assignments: [],
                visible: true
            };
            const resultNew = await appManager.ModalHooks.OdEditModal.open({
                matrix: newMatrix,
                center: [view.center.lng, view.center.lat],
                zoom: view.zoom,
            });
            if (resultNew === "cancelled")
                return;
            await this.addMatrix(newMatrix, [
                {
                    profile: "bicycle.commute",
                    visible: true,
                    color: RandomColor.Next(),
                },
            ]);
            return;
        }
        if (result === "random-locations") {
            const result = await appManager.ModalHooks.RandomPointsModal.open(view);
            if (typeof result === "undefined")
                return;
            await this.addMatrix(result.matrix, [
                {
                    profile: result.profile,
                    visible: true,
                    color: RandomColor.Next(),
                },
            ]);
            return;
        }
        if (result === "demand-model") {
            const result = await appManager.ModalHooks.DemandModal.open(view);
            if (typeof result === "undefined")
                return;
            await this.addMatrix(result, [
                {
                    profile: "car.fast",
                    visible: true,
                    color: RandomColor.Next(),
                },
            ]);
            return;
        }
        if (result === "trips-layers") {
            const result = await appManager.ModalHooks.TripsModal.open(view);
            if (typeof result === "undefined")
                return;
            await this.addMatrix(result, [
                {
                    profile: "bicycle.commute",
                    visible: true,
                    color: RandomColor.Next(),
                },
            ]);
            return;
        }
    }
    /**
     * Adds a new matrix layer for the given od matrix.
     *
     * @param matrix The matrix.
     * @param profile The initial profile to use.
     */
    async addMatrix(newMatrix, assignments) {
        assignments ?? [{
                profile: "bicycle.commute",
                visible: true,
                color: RandomColor.Next()
            }];
        const token = await appManager.api.getAccessToken();
        // first make sure trips layer is created.
        await this.save();
        // save matrix if needed.
        const matrix = await appManager.api.projects.createMatrix({
            data: {
                name: newMatrix.name,
                commitCommit: "Created new matrix.",
                elements: newMatrix.elements,
                locations: newMatrix.locations,
                properties: newMatrix.properties,
            },
            tripScenarioId: this.data.id,
            accessToken: token
        });
        // create assignments.
        const matrices = get(this.matrices);
        for (let pl = 0; pl < assignments.length; pl++) {
            const profileLayer = assignments[pl];
            const assignment = {
                color: profileLayer.color,
                profile: profileLayer.profile,
                visible: profileLayer.visible,
                visualizationType: profileLayer.visualizationType
            };
            const layerId = await appManager.api.projects.createMatrixAssignment({
                tripScenarioId: this.data.id,
                matrixId: matrix.id,
                data: assignment,
                accessToken: token,
            });
            matrix.assignments.push({
                id: layerId,
                profile: assignment.profile,
                visible: assignment.visible,
                color: assignment.color,
                visualizationType: assignment.visualizationType
            });
        }
        const matrixLayer = new MatrixRowManager(this, matrix);
        matrixLayer.addToMap(this.map);
        matrices.push(matrixLayer);
        this.matrices.set(matrices);
        this.refreshTrips();
    }
    /**
     * Notifies that a matrix was updated.
     */
    async matrixUpdated() {
        const matrices = get(this.matrices);
        this.matrices.set(matrices);
        this.refreshTrips();
    }
    /**
     * The statistics on all visible routes.
     *
     * @returns The statistics.
     */
    getStats() {
        return this.mapLayerManager.getStats();
    }
    /**
     * Get the raw features for the current matrix
     * This can have multiple features for one segment, namely (forward|backward)*(profile)
     */
    getFeatures() {
        return this.mapLayerManager.getFeatures();
    }
    /**
     * Returns one feature per segment
     * Every segment will have:
     * - The OSM-properties
     * - Scores for bicycle aspects
     * - Count per profile (total, forward, backward)
     * - Count per vehicle (total, forward, backward)
     */
    getFusedFeatures() {
        const manySegments = this.getFeatures();
        const perId = new Map();
        for (const segment of manySegments) {
            const tail = segment.properties["_tail_offset"] ?? 0;
            const head = segment.properties["_head_offset"] ?? 65536;
            const forward = segment.properties["_forward"] ?? "";
            let id = `${segment.properties["_id"]}_${forward}`;
            if (tail !== 0 || head !== 65536) {
                id = `${id}_${tail}_${head}`;
            }
            const maxCount = Number(segment.properties["_max_count"] ?? 0);
            if (maxCount <= 0) {
                continue;
            }
            if (!perId.has(id)) {
                const properties = {};
                for (const k in segment.properties) {
                    properties[k] = segment.properties[k];
                }
                const newFeature = turf.feature(segment.geometry, properties);
                perId.set(id, newFeature);
            }
        }
        return Array.from(perId.values());
    }
    /**
     * Gets the available profiles.
     */
    getAvailableProfiles() {
        return this.options.profiles;
    }
    /**
     * The matrix layers.
     *
     * @returns The matrix layers.
     */
    getMatrixLayers() {
        return this.matrices;
    }
    /**
     * Gets the profile layers.
     *
     * @returns The profile layers.
     */
    getAssignmentRowManagersStore() {
        return this.assignmentRows;
    }
    /**
     * Gets the commit id of the reference scenario.
     *
     * @returns The commit id.
     */
    getReferenceBranchCommitId() {
        return this.parent.getReferenceBranchCommitIdStore();
    }
    /**
     * Gets the commit id.
     *
     * @returns The commit id.
     */
    getBranchCommitIdStore() {
        return this.parent.getBranchCommitIdStore();
    }
    /**
     * Deletes the matrix with the given id and removes it.
     * @param id The id.
     */
    async deleteMatrix(id) {
        const token = await appManager.api.getAccessToken();
        await appManager.api.projects.deleteMatrix({
            tripScenarioId: this.data.id,
            id: id,
            data: {
                commitComment: "Deleted matrix"
            },
            accessToken: token
        });
        const matrices = get(this.matrices);
        //console.log(this.matrices);
        matrices.splice(matrices.findIndex(x => get(x.id) == id), 1);
        this.matrices.set(matrices);
        this.refreshTrips();
    }
    async save() {
        const token = await appManager.api.getAccessToken();
        // if (typeof this.data.id === "undefined") {
        //     const data = await appManager.api.projects.createTripsLayer({
        //         accessToken: token,
        //         tripsLayer: this.data
        //     });
        //     this.data.id = data.id;
        //     return;
        // }
        const result = await appManager.api.projects.updateTripScenario({
            id: this.data.id,
            data: {
                name: this.data.name
            },
            accessToken: token
        });
    }
    /**
     * Called when the matrices set is updated.
     *
     * @param matrices The new matrices set.
     */
    onMatricesChanged(matrices) {
        //console.log(`matrices changed: ${matrices}`);
        // add new profile layers listeners.
        matrices.forEach(m => {
            const matrixId = get(m.id);
            if (this.assignmentRowsPerMatrix.containsKey(matrixId))
                return;
            const profileLayers = m.getMatrixAssignmentRows();
            profileLayers.subscribe(ls => {
                this.onProfileLayersChanged(matrixId, ls);
            });
        });
        this.refreshTrips();
    }
    /**
     * Called when the profile layer set in one of the matrices is updated.
     *
     * @param matrixLayerId The matrix layer id.
     * @param assignmentRows The new profile layer set.
     */
    onProfileLayersChanged(matrixLayerId, assignmentRows) {
        this.assignmentRowsPerMatrix.set(matrixLayerId, assignmentRows);
        const allLayers = [];
        this.assignmentRowsPerMatrix.forEach((key, value) => {
            value.forEach(v => allLayers.push(v));
        });
        this.assignmentRows.set(allLayers);
    }
}
